import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, EmptyState, Field, Select } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { MERCHANTS_PAGE_SIZE, useMerchantsQuery } from '../api';
import { MerchantTable } from '../components/MerchantTable';
import {
  MERCHANT_STATUSES,
  MERCHANT_STATUS_LABEL_KEY,
  SCOPE_ALL,
  SCOPE_UNASSIGNED,
  type MerchantStatus,
  type MerchantSummary,
} from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { useAssignableAdminsQuery } from '@/features/portfolios/api';
import {
  AssignmentDialog,
  type AssignmentTarget,
} from '@/features/portfolios/components/AssignmentDialog';
import { adminName } from '@/features/portfolios/types';
import { consultationReason } from '@/lib/consultation-reason';
import { useSession } from '@/lib/session';

/**
 * `GET /api/v1/admin/merchants` requires a stated `reason` (≥10 characters) even to LIST - every platform-admin read is audited
 * (`merchant-admin.dto.ts`'s own schema). The list opens straight away with the console's default reason; a reason typed in the
 * field replaces it (`consultationReason`).
 *
 * WHAT THE LIST CONTAINS depends on who asks (lot P2), and the page says so in plain words above the table:
 *   * an administrator sees its PORTFOLIO - the merchants it created or that a superadmin assigned to it - and nothing else; a
 *     portfolio that is still empty gets an explanation, not a bare « no result »;
 *   * the superadmin sees EVERY shop, with its manager, can narrow by administrator or to the shops nobody manages, and can assign
 *     them (one by one or a selection). It never approves or refuses a shop: the managing administrator does.
 *
 * Whatever this page offers is a convenience. The server (and the database beneath it) decide what each caller reaches.
 */
export function MerchantsPage(): ReactElement {
  const { t } = useI18n();
  const { roles } = useSession();
  const isSuperadmin = roles.includes('superadmin');
  const [params] = useSearchParams();

  // The KPI dashboard's cards link here with `?status=active|pending|suspended` (and `?scope=...`).
  const [statusFilter, setStatusFilter] = useState<MerchantStatus | ''>(() => {
    const requested = params.get('status');
    return MERCHANT_STATUSES.find((status) => status === requested) ?? '';
  });
  const [search, setSearch] = useState('');
  // The portfolios screen links here with `?scope=unassigned` or `?scope=<administrator id>`; only a superadmin's scope has any effect.
  const [scope, setScope] = useState(params.get('scope') ?? SCOPE_ALL);
  const [reason, setReason] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [assignTarget, setAssignTarget] = useState<AssignmentTarget | null>(null);

  const effectiveScope = isSuperadmin ? scope : SCOPE_ALL;
  const listReason = consultationReason(reason);
  const filters = { status: statusFilter, search, scope: effectiveScope };
  const merchantsQuery = useMerchantsQuery(filters, listReason, offset);
  const adminsQuery = useAssignableAdminsQuery(isSuperadmin);

  const statusOptions = [
    { value: '', label: t('merchant.orders.filterAllStatuses') },
    ...MERCHANT_STATUSES.map((status) => ({
      value: status,
      label: t(MERCHANT_STATUS_LABEL_KEY[status]),
    })),
  ];
  const scopeOptions = [
    { value: SCOPE_ALL, label: t('admin.merchants.scopeAll') },
    { value: SCOPE_UNASSIGNED, label: t('admin.merchants.scopeUnassigned') },
    ...(adminsQuery.data ?? []).map((admin) => ({ value: admin.userId, label: adminName(admin) })),
  ];

  // Any change of filter or reason is a new query - start it at the first page, with nothing selected.
  useEffect(() => {
    setOffset(0);
    setSelected(new Set());
  }, [statusFilter, search, effectiveScope, listReason]);

  const page = merchantsQuery.data;
  const hasPrev = offset > 0;
  const hasNext = page !== undefined && offset + page.merchants.length < page.total;
  const searchActive = search.trim().length >= 2;
  const filtersActive = statusFilter !== '' || searchActive || effectiveScope !== SCOPE_ALL;

  function toggle(tenantId: string): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(tenantId)) next.delete(tenantId);
      else next.add(tenantId);
      return next;
    });
  }

  function toggleAll(merchants: readonly MerchantSummary[]): void {
    setSelected((current) =>
      merchants.every((merchant) => current.has(merchant.tenantId))
        ? new Set()
        : new Set(merchants.map((merchant) => merchant.tenantId)),
    );
  }

  function emptyState(): ReactElement {
    if (!isSuperadmin && !filtersActive) {
      // An administrator whose portfolio is still empty: say WHY, and what to do - not a bare « no result ».
      return (
        <EmptyState
          title={t('admin.merchants.portfolioEmptyTitle')}
          description={t('admin.merchants.portfolioEmptyDescription')}
          action={
            <Link to="/organisations">
              <Button>{t('admin.merchants.portfolioEmptyAction')}</Button>
            </Link>
          }
        />
      );
    }
    if (
      isSuperadmin &&
      effectiveScope === SCOPE_UNASSIGNED &&
      statusFilter === '' &&
      !searchActive
    ) {
      return <EmptyState title={t('admin.merchants.noUnassignedTitle')} />;
    }
    return <EmptyState title={t('admin.merchants.emptyForFilter')} />;
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">{t('admin.nav.merchants')}</h1>
        <p className="text-sm text-inkFaint">{t('admin.merchants.subtitle')}</p>
      </div>

      <p role="note" className="rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">
        {isSuperadmin ? t('admin.merchants.scopeSuperInfo') : t('admin.merchants.scopeAdminInfo')}
      </p>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-line bg-surface p-4">
        <div className="min-w-64 flex-1">
          <Field label={t('admin.common.reasonConsultationLabel')}>
            {(bind) => (
              <input
                {...bind}
                type="text"
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
                maxLength={500}
                className="ecom-input"
                placeholder={t('admin.merchants.reasonPlaceholder')}
              />
            )}
          </Field>
        </div>
        <div className="min-w-48 flex-1">
          <Field label={t('admin.merchants.searchLabel')}>
            {(bind) => (
              <input
                {...bind}
                type="search"
                name="merchant-search"
                // Without it the browser filled the field with a saved e-mail (« admin-dev@… ») and the list showed nothing.
                autoComplete="off"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
                maxLength={100}
                className="ecom-input"
                placeholder={t('admin.merchants.searchPlaceholder')}
              />
            )}
          </Field>
        </div>
        {isSuperadmin ? (
          <Select
            options={scopeOptions}
            value={scope}
            onChange={(event) => {
              setScope(event.target.value);
            }}
            aria-label={t('admin.merchants.scopeLabel')}
          />
        ) : null}
        <Select
          options={statusOptions}
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value as MerchantStatus | '');
          }}
          aria-label={t('admin.merchants.filterStatusAria')}
        />
      </div>

      {merchantsQuery.isPending ? (
        <TableSkeleton columns={4} />
      ) : merchantsQuery.isError ? (
        <EmptyState
          title={
            merchantsQuery.error.status === 403
              ? t('merchant.common.accessDenied')
              : t('admin.merchants.loadError')
          }
          description={
            merchantsQuery.error.status === 403
              ? t('admin.merchants.accessDeniedDescription')
              : t(resolveErrorMessageKey(merchantsQuery.error))
          }
        />
      ) : page === undefined || page.merchants.length === 0 ? (
        emptyState()
      ) : (
        <>
          {isSuperadmin && selected.size > 0 ? (
            <div className="flex items-center justify-between rounded-md bg-surface2 px-3 py-2">
              <span className="text-sm text-inkSoft">
                {t('admin.merchants.assignSelection', { count: selected.size })}
              </span>
              <Button
                size="sm"
                onClick={() => {
                  setAssignTarget({ kind: 'merchants', tenantIds: [...selected] });
                }}
              >
                {t('admin.merchants.action.assign')}
              </Button>
            </div>
          ) : null}
          <MerchantTable
            merchants={page.merchants}
            selection={isSuperadmin ? { selected, toggle, toggleAll } : undefined}
            onAssign={
              isSuperadmin
                ? (merchant) => {
                    setAssignTarget({ kind: 'merchants', tenantIds: [merchant.tenantId] });
                  }
                : undefined
            }
          />
          <div className="flex items-center justify-between text-sm text-inkFaint">
            <span>
              {t('admin.merchants.paginationSummary', {
                from: offset + 1,
                to: offset + page.merchants.length,
                total: page.total,
              })}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasPrev}
                onClick={() => {
                  setOffset((current) => Math.max(0, current - MERCHANTS_PAGE_SIZE));
                }}
              >
                {t('merchant.orders.prev')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasNext}
                onClick={() => {
                  setOffset((current) => current + MERCHANTS_PAGE_SIZE);
                }}
              >
                {t('merchant.orders.next')}
              </Button>
            </div>
          </div>
        </>
      )}

      <AssignmentDialog
        target={assignTarget}
        onClose={() => {
          setAssignTarget(null);
          setSelected(new Set());
        }}
      />
    </div>
  );
}
